import { z } from 'zod';
import { uuidSchema, isoDateSchema, isoDateTimeSchema, timestampedSchema } from '../schemas/common';

export const competitionInputSchema = z
  .object({
    name: z.string().trim().min(1).max(200),
    organizer: z.string().trim().max(200).optional(),
    venue_name: z.string().trim().max(200).optional(),
    season: z.string().trim().min(1).max(80),
    start_date: isoDateSchema,
    end_date: isoDateSchema,
    notes: z.string().trim().max(2000).optional(),
  })
  .refine((c) => c.end_date >= c.start_date, {
    message: 'end_date must be on or after start_date',
    path: ['end_date'],
  });
export type CompetitionInput = z.infer<typeof competitionInputSchema>;

export const competitionSchema = timestampedSchema.extend({
  name: z.string(),
  organizer: z.string().nullable(),
  venue_name: z.string().nullable(),
  venue_address: z.unknown().nullable(),
  start_date: isoDateSchema,
  end_date: isoDateSchema,
  season: z.string(),
  notes: z.string().nullable(),
  deleted_at: isoDateTimeSchema.nullable(),
});
export type Competition = z.infer<typeof competitionSchema>;

// Competition routine — a dance entered in a competition.
export const competitionRoutineInputSchema = z.object({
  competition_id: uuidSchema,
  dance_id: uuidSchema,
  division: z.string().trim().max(80).optional(),
  level: z.string().trim().max(40).optional(),
  entry_fee_cents: z.number().int().nonnegative().default(0),
  competition_entry_number: z.string().trim().max(40).optional(),
  scheduled_at: isoDateTimeSchema.optional(),
});
export type CompetitionRoutineInput = z.infer<typeof competitionRoutineInputSchema>;

// Itinerary item.
export const itineraryKindSchema = z.enum([
  'call',
  'travel',
  'hotel',
  'meal',
  'rehearsal',
  'other',
]);
export type ItineraryKind = z.infer<typeof itineraryKindSchema>;

export const itineraryInputSchema = z
  .object({
    competition_id: uuidSchema,
    kind: itineraryKindSchema,
    label: z.string().trim().min(1).max(200),
    start_at: isoDateTimeSchema,
    end_at: isoDateTimeSchema.optional(),
    location: z.string().trim().max(200).optional(),
    notes: z.string().trim().max(2000).optional(),
  })
  .refine((i) => !i.end_at || i.end_at >= i.start_at, {
    message: 'end_at must be on or after start_at',
    path: ['end_at'],
  });
export type ItineraryInput = z.infer<typeof itineraryInputSchema>;

// Routine placement (manual score entry).
export const placementInputSchema = z.object({
  competition_routine_id: uuidSchema,
  placement: z.number().int().min(1).max(1000).optional(),
  total_competitors: z.number().int().min(1).max(10000).optional(),
  score: z.number().min(0).max(1000).optional(),
  award: z.string().trim().max(120).optional(),
  notes: z.string().trim().max(2000).optional(),
});
export type PlacementInput = z.infer<typeof placementInputSchema>;
